import React, { useEffect, useMemo, useReducer } from 'react';
import { useIntl } from 'react-intl';

import { useOptionalMessage } from 'Hooks/useOptionalMessage';
import addOnStore from 'Models/AddOnStore';
import AdditionalBookingSectionEntity from 'Models/Entities/AdditionalBookingSectionEntity';
import { FreightTabDto } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { getFerryAndRouteIds } from 'Util/_HumanWritten/BookingWizard/BookingWizardWrapUtils';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { WizardStepHeader } from '../Shared/WizardStepHeader';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { FreightBookingWizardData } from '../../BookingWizardData/FreightBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { useWizardFetchedDataStore } from '../../BookingWizardState/BookingWizardFetchedData';
import { SelectedTrips } from '../../BookingWizardWrap';
import {
	BulkFreightDetailsFormActionCreators,
	bulkFreightDetailsFormReducer,
	initialBulkFreightDetailsFormState,
} from './BulkFreightDetailsFormState';
import ShipmentCard from './ShipmentCard';
import { appConfigStore } from 'Models/AppConfigStore';

interface BulkShipmentTabProps {
	selectedTrips: SelectedTrips;
}

export default function BulkShipmentTab({ selectedTrips }: BulkShipmentTabProps) {
	const freightTabData = useWizardFetchedDataStore(x => x.freightTabData);
	const wizardData = useBookingWizardDataStore(x => x.wizardData);

	const addOnSections = useMemo(() => {
		const ids = getFerryAndRouteIds(selectedTrips, 'all');
		return addOnStore.getAddOnSectionsByFerriesAndRoutes(ids);
	}, [selectedTrips]);

	if (!freightTabData) {
		console.error('Freight tab data is missing');
		return <></>;
	}

	if (wizardData.innerWizardData.type !== 'freight') {
		console.error('Booking wizard must be for freight');
		return <></>;
	}

	return (
		<LoadedState
			freightTabData={freightTabData}
			freightWizardData={wizardData.innerWizardData}
			addOnSections={addOnSections}
		/>
	);
}

interface LoadedStateProps {
	freightTabData: FreightTabDto;
	freightWizardData: FreightBookingWizardData;
	addOnSections: AdditionalBookingSectionEntity[];
}

function LoadedState({ freightTabData, freightWizardData, addOnSections }: LoadedStateProps) {
	const intl = useIntl();
	const fmtOptional = useOptionalMessage();
	const wizardData = useBookingWizardDataStore(x => x.wizardData);
	const setWizardData = useBookingWizardDataStore(x => x.setWizardData);

	const [state, dispatch] = useReducer(
		bulkFreightDetailsFormReducer,
		initialBulkFreightDetailsFormState(
			appConfigStore.freightDimensionsHiddenOnStaticPriceEnabled,
			freightTabData,
			freightWizardData.items,
		),
	);

	useEffect(() => {
		const newData = new BookingWizardData(wizardData);
		if (newData.innerWizardData.type === 'freight') {
			newData.innerWizardData.items = state.items;
			setWizardData(newData);
		}
	}, [state]);

	if (!wizardData.departingJourney) {
		return <></>;
	}

	const journey = wizardData.departingJourney;

	const shipmentSubtitle = fmtOptional({
		id: 'freight-booking-wizard__shipment-details__subtitle',
		defaultMessage: "Describe the freight you're booking.",
	});

	return (
		<div className="booking-wizard-tab shipment-details-tab mb-xxxl">
			<WizardStepHeader
				iconName="inventory_2"
				title={intl.formatMessage({
					id: 'freight-booking-wizard__shipment-details__header',
					defaultMessage: 'Enter shipment details',
				})}
				subtitle={shipmentSubtitle || undefined}
			/>
			<div className="flex flex-col gap-3">
				{freightWizardData.items.map(item => {
					return (
						<ShipmentCard
							key={item.id}
							isExpanded={item.id === state.expandedId}
							freightElements={freightTabData}
							addOnSections={addOnSections}
							journey={journey}
							userId={wizardData.userId}
							state={item}
							dispatch={dispatch}
							hasMultipleItems={freightWizardData.items.length > 1}
						/>
					);
				})}
				<button
					type="button"
					className="booking-section-add-button"
					onClick={() => dispatch(BulkFreightDetailsFormActionCreators.addNewDetail())}
					data-test-id="freight-shipment-add-another-booking"
				>
					<span className="booking-section-heading__icon">
						<Icon name="add" useMaterialIcons />
					</span>
					Add another booking
				</button>
			</div>
		</div>
	);
}
